import * as LucideIcons from "lucide-react";

const NODE_REGISTRY = {
    // Triggers
    webhook: { icon: LucideIcons.Webhook, color: "#8B5CF6", label: "Webhook" },
    schedule: { icon: LucideIcons.Clock, color: "#F59E0B", label: "Schedule" },
    email: { icon: LucideIcons.Mail, color: "#EF4444", label: "Email" },
    whatsapp: { icon: LucideIcons.Phone, color: "#22C55E", label: "WhatsApp" },
    // Core
    http_request: { icon: LucideIcons.Globe, color: "#3B82F6", label: "HTTP Request" },
    code: { icon: LucideIcons.Code, color: "#6366F1", label: "Code" },
    if_condition: { icon: LucideIcons.GitBranch, color: "#F97316", label: "IF" },
    switch: { icon: LucideIcons.GitBranch, color: "#F97316", label: "Switch" },
    set_fields: { icon: LucideIcons.FileText, color: "#10B981", label: "Set Fields" },
    loop: { icon: LucideIcons.Repeat, color: "#8B5CF6", label: "Loop" },
    merge: { icon: LucideIcons.Merge, color: "#6366F1", label: "Merge" },
    split: { icon: LucideIcons.Split, color: "#6366F1", label: "Split" },
    // AI
    openai: { icon: LucideIcons.Bot, color: "#10B981", label: "OpenAI" },
    claude: { icon: LucideIcons.Brain, color: "#D97706", label: "Claude" },
    gemini: { icon: LucideIcons.Sparkles, color: "#3B82F6", label: "Gemini" },
    langchain: { icon: LucideIcons.Link, color: "#1C3C3C", label: "LangChain" },
    // Integrations
    google_sheets: { icon: LucideIcons.Table, color: "#22C55E", label: "Google Sheets" },
    gmail: { icon: LucideIcons.Mail, color: "#EF4444", label: "Gmail" },
    slack: { icon: LucideIcons.MessageSquare, color: "#611F69", label: "Slack" },
    telegram: { icon: LucideIcons.Send, color: "#229ED9", label: "Telegram" },
    google_drive: { icon: LucideIcons.FolderOpen, color: "#F59E0B", label: "Google Drive" },
    notion: { icon: LucideIcons.Notebook, color: "#000000", label: "Notion" },
    airtable: { icon: LucideIcons.LayoutGrid, color: "#18BFFF", label: "Airtable" },
    postgresql: { icon: LucideIcons.Database, color: "#336791", label: "PostgreSQL" },
    supabase: { icon: LucideIcons.Database, color: "#3ECF8E", label: "Supabase" },
    discord: { icon: LucideIcons.Hash, color: "#5865F2", label: "Discord" },
    hubspot: { icon: LucideIcons.Settings, color: "#FF7A59", label: "HubSpot" },
    stripe: { icon: LucideIcons.CreditCard, color: "#635BFF", label: "Stripe" },
    github: { icon: LucideIcons.Github, color: "#181717", label: "GitHub" },
    jira: { icon: LucideIcons.Ticket, color: "#0052CC", label: "Jira" },
    twitter: { icon: LucideIcons.Twitter, color: "#1DA1F2", label: "Twitter" },
    linkedin: { icon: LucideIcons.Linkedin, color: "#0A66C2", label: "LinkedIn" },
    shopify: { icon: LucideIcons.ShoppingCart, color: "#96BF48", label: "Shopify" },
    // Generic
    transform: { icon: LucideIcons.Settings, color: "#6366F1", label: "Transform" },
    filter: { icon: LucideIcons.Filter, color: "#8B5CF6", label: "Filter" },
    notify: { icon: LucideIcons.Bell, color: "#F59E0B", label: "Notify" },
    store: { icon: LucideIcons.HardDrive, color: "#10B981", label: "Store" },
    fetch: { icon: LucideIcons.Search, color: "#3B82F6", label: "Fetch" },
    parse: { icon: LucideIcons.AlignLeft, color: "#6366F1", label: "Parse" },
    validate: { icon: LucideIcons.CheckCircle, color: "#22C55E", label: "Validate" },
    format: { icon: LucideIcons.FileText, color: "#8B5CF6", label: "Format" },
    custom: { icon: LucideIcons.Settings, color: "#6B7280", label: "Custom" },
    // Fallbacks
    trigger: { icon: LucideIcons.Zap, color: "#F59E0B", label: "Trigger" },
    default: { icon: LucideIcons.Settings, color: "#6B7280", label: "Step" },
};

export function getNodeRegistryEntry(iconKey) {
    if (NODE_REGISTRY[iconKey]) return NODE_REGISTRY[iconKey];

    // Any PascalCase Lucide icon (e.g. "Truck", "HeartPulse")
    const lucideIcon = LucideIcons[iconKey];
    if (lucideIcon) return { icon: lucideIcon, color: "#6B7280", label: iconKey };

    return NODE_REGISTRY.default;
}

export function inferIconFromType(step) {
    const meta = step.config?.nodeMetadata;
    if (meta?.icon) {
        const entry = getNodeRegistryEntry(meta.icon);
        if (entry !== NODE_REGISTRY.default || meta.icon === "default") return meta.icon;
    }

    const type = (step.type || "").toLowerCase();
    const name = (step.name || "").toLowerCase();
    const integration = (step.config?.integrationName || "").toLowerCase();

    if (integration && NODE_REGISTRY[integration]) return integration;
    if (type.includes("trigger") || name.includes("trigger")) return "trigger";
    if (type.includes("webhook") || name.includes("webhook")) return "webhook";
    if (type.includes("schedule") || name.includes("schedule")) return "schedule";
    if (type.includes("code") || type.includes("code_editor")) return "code";
    if (name.includes("openai") || name.includes("gpt")) return "openai";
    if (name.includes("claude") || name.includes("anthropic")) return "claude";
    if (name.includes("gemini")) return "gemini";
    if (name.includes("slack")) return "slack";
    if (name.includes("gmail")) return "gmail";
    if (name.includes("sheet")) return "google_sheets";
    if (name.includes("notion")) return "notion";
    if (name.includes("telegram")) return "telegram";
    if (name.includes("postgres") || name.includes("sql")) return "postgresql";
    if (name.includes("supabase")) return "supabase";
    if (name.includes("discord")) return "discord";
    if (name.includes("stripe")) return "stripe";
    if (name.includes("github")) return "github";
    if (name.includes("shopify")) return "shopify";

    return "default";
}

export function getNodeType(step) {
    const meta = step.config?.nodeMetadata;
    const iconKey = meta?.icon || "";
    const name = (step.name || "").toLowerCase();
    const type = (step.type || "").toLowerCase();

    if (type.includes("trigger")) {
        return "trigger";
    }
    if (iconKey === "if_condition" || iconKey === "switch" || name.includes("switch") || name.includes("conditional")) {
        return "conditional";
    }
    return "canvas";
}

export default NODE_REGISTRY;
